import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function ClaimPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [listing, setListing] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/claims/verify/${token}`)
      .then(({ data }) => {
        setListing(data);
        setName(data.name || "");
      })
      .catch((e) => {
        setErr(e.response?.data?.detail || "Invalid or expired link.");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/claims/complete", { token, password, name });
      setUser(data.user);
      toast.success("Listing claimed! Welcome to Fixus.");
      nav("/dashboard");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not complete claim.");
    } finally {
      setSubmitting(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">Link issue</h1>
        <p className="text-slate-600 mb-6" data-testid="claim-error">{err}</p>
        <Button onClick={() => nav("/tradespeople")} variant="outline" className="border-blue-900 text-blue-900">
          Browse the directory
        </Button>
      </div>
    );
  }
  if (!listing) {
    return <div className="max-w-md mx-auto px-4 py-16 text-slate-500">Verifying link…</div>;
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Claim listing</div>
      <h1 className="font-display font-bold text-3xl text-slate-900 mb-2">
        Welcome, {listing.name?.split(" ")[0]}
      </h1>
      <p className="text-slate-600 mb-6">Set a password to take ownership of your Fixus listing.</p>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-5">
          <div className="font-display font-semibold text-slate-900 text-lg">{listing.business_name || listing.name}</div>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-slate-500">
            <MapPin className="w-3.5 h-3.5" /> {listing.city}, {listing.province}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(listing.categories || []).map((c) => (
              <Badge key={c} variant="outline" className="border-slate-200 text-slate-700 font-normal">
                {c}
              </Badge>
            ))}
          </div>
          <div className="mt-3 inline-flex items-center gap-1 text-sm text-blue-900">
            <BadgeCheck className="w-4 h-4" /> A Verified badge will be added once you claim.
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Your name (as shown to homeowners)</Label>
              <Input
                data-testid="claim-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Email (used to sign in)</Label>
              <Input value={listing.email || ""} readOnly className="mt-1.5 bg-slate-50" />
            </div>
            <div>
              <Label>Choose a password</Label>
              <Input
                data-testid="claim-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="At least 6 characters"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              data-testid="claim-submit"
              disabled={submitting || !password || !name}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
            >
              {submitting ? "Claiming…" : "Claim my listing"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              By claiming, you confirm you&apos;re authorized to represent this business. We may remove listings on request — reply to any Fixus email or contact support.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
